import { NavLink, Outlet } from "react-router-dom";
import { useLock } from "../context/LockContext";
import {
  LayoutDashboard,
  Receipt,
  TrendingDown,
  Sprout,
  Settings,
  Lock,
  Leaf,
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", short: "Home", testId: "nav-dashboard" },
  { to: "/expenses", icon: Receipt, label: "Income & Expenses", short: "Money", testId: "nav-expenses" },
  { to: "/debt", icon: TrendingDown, label: "Debt Tracker", short: "Debt", testId: "nav-debt" },
  { to: "/wealth", icon: Sprout, label: "Wealth Building", short: "Wealth", testId: "nav-wealth" },
  { to: "/settings", icon: Settings, label: "Settings", short: "Settings", testId: "nav-settings" },
];

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all text-sm ${
        isActive
          ? "bg-nest-surface-5 text-nest-ink font-medium"
          : "text-nest-soft hover:bg-nest-surface-2"
      }`
    }
  >
    <Icon size={18} strokeWidth={1.5} />
    <span>{label}</span>
  </NavLink>
);

const TabItem = ({ to, icon: Icon, short, testId }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`tab-${testId}`}
    className={({ isActive }) =>
      `flex-1 flex flex-col items-center gap-1 pt-2 pb-1 text-[10px] tracking-wide ${
        isActive ? "text-nest-brand font-semibold" : "text-nest-muted"
      }`
    }
  >
    <Icon size={22} strokeWidth={1.5} />
    <span>{short}</span>
  </NavLink>
);

export default function Layout() {
  const { lock } = useLock();
  return (
    <div className="min-h-[100dvh] flex" data-testid="app-layout">
      {/* Sidebar (tablet / desktop) */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-nest-line nest-glass p-6 flex-col">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 rounded-2xl nest-brand-fill text-white flex items-center justify-center">
            <Leaf size={18} strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-heading text-lg font-medium leading-none">
              Nest
            </div>
            <div className="text-[10px] uppercase tracking-widest text-nest-muted mt-1">
              Finance
            </div>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {NAV.map((n) => (
            <NavItem key={n.to} {...n} />
          ))}
        </nav>
        <div className="pt-6 border-t border-nest-line mt-6">
          <button
            data-testid="lock-btn"
            onClick={lock}
            className="flex items-center gap-2 text-sm text-nest-soft hover:text-nest-warn transition-colors"
          >
            <Lock size={16} strokeWidth={1.5} />
            Lock
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center justify-between px-5 pt-3 nest-safe-top">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl nest-brand-fill text-white flex items-center justify-center">
              <Leaf size={14} strokeWidth={1.5} />
            </div>
            <span className="font-heading font-medium">Nest</span>
          </div>
          <button onClick={lock} className="text-nest-muted p-2 -mr-2" aria-label="Lock" data-testid="lock-btn-mobile">
            <Lock size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-28 md:p-12">
          <Outlet />
        </div>
      </main>

      {/* Bottom tab bar (phone) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 nest-glass-bar border-t border-nest-line flex nest-safe-bottom">
        {NAV.map((n) => (
          <TabItem key={n.to} {...n} />
        ))}
      </nav>
    </div>
  );
}
